/* participants.js — Iter-285 / Workstream #5
 *
 * Participant accountability engine. Pure functions, no React.
 *
 * The brief: "Make every transaction participant-owned and fully
 * accountable." This module is the single source of truth for:
 *
 *   • The canonical participant role vocabulary
 *     (Buyer / Seller / Buyer Solicitor / Seller Solicitor /
 *      Mortgage Broker / Negotiator / Auditor / Other).
 *
 *   • The default OWNER for each document-request template
 *     (Management Pack → Seller Solicitor, Searches → Buyer
 *     Solicitor, Mortgage Offer → Mortgage Broker, Enquiries →
 *     Seller Solicitor, …).
 *
 *   • The blocking-participant computation — given a deal and
 *     its outstanding doc requests, who is the bottleneck and
 *     for how long?
 *
 *   • The participant-derived signals fed into the iter-281
 *     health scorer so a silent solicitor weighs more than a
 *     silent admin chase.
 *
 *   • Activity-row attribution — every chase / fulfilment /
 *     automation row should carry the participant role so the
 *     feed can group/filter by accountability.
 *
 * Flag-gated via `REACT_APP_PARTICIPANT_ACCOUNTABILITY_V1`. */

/* ─── Canonical role vocabulary ──────────────────────────────── */
export const PARTICIPANT_ROLES = Object.freeze([
  "Buyer",
  "Seller",
  "Buyer Solicitor",
  "Seller Solicitor",
  "Mortgage Broker",
  "Negotiator",
  "Auditor",
  "Other",
]);

/* ─── Template → default owner ───────────────────────────────────
 *
 * Mirrors the iter-282 DOC_TEMPLATE_TO_JOURNEY_STEP map but for
 * accountability. When a document request is created without an
 * explicit `requested_from_role`, this table is consulted so the
 * request is still attached to the canonical responsible
 * participant. */
export const TEMPLATE_DEFAULT_OWNER = Object.freeze({
  aml:                  "Buyer",
  memo_of_sale:         "Negotiator",
  management_pack:      "Seller Solicitor",
  contract_pack:        "Seller Solicitor",
  draft_contract:       "Seller Solicitor",
  searches:             "Buyer Solicitor",
  survey_report:        "Buyer",
  enquiries_responses:  "Seller Solicitor",
  mortgage_offer:       "Mortgage Broker",
  signed_contract:      "Buyer Solicitor",
  exchange_confirmation: "Buyer Solicitor",
  id_check:             "Buyer",
  bank_statement:       "Buyer",
  other:                "Other",
});

/* Legal participants — used for the "solicitor silence" signal. */
const LEGAL_ROLES = new Set(["Buyer Solicitor", "Seller Solicitor"]);

const MS_PER_DAY = 86_400_000;

function _parseIso(s) { const t = Date.parse(s || ""); return Number.isFinite(t) ? t : null; }

/* ─── Owner resolver ──────────────────────────────────────────────
 *
 * Returns the canonical role responsible for fulfilling the
 * request. Consults `requested_from_role` first (operator
 * override), then falls back to the template default. */
export function ownerFor(request) {
  if (!request) return null;
  const explicit = String(request.requested_from_role || "").trim();
  if (explicit && PARTICIPANT_ROLES.includes(explicit)) return explicit;
  const tpl = request.template_id;
  return tpl && TEMPLATE_DEFAULT_OWNER[tpl] != null ? TEMPLATE_DEFAULT_OWNER[tpl] : "Other";
}

/* ─── Who is blocking this deal? ─────────────────────────────────
 *
 * Given the deal's outstanding doc requests, returns a list of
 * `{ role, names: [...], outstandingCount, oldestDays, severity }`
 * sorted by oldest-first. `severity` is calibrated against the
 * iter-281 / 282 / 283 health vocabulary:
 *   • rose    — at least one request is ≥ 7d old (final-chase tier)
 *   • amber   — at least one request is ≥ 3d old
 *   • emerald — fresh requests only
 *
 * Resolved/fulfilled requests are excluded. */
export function blockingParticipantsFor({ deal, requests, nowIso } = {}) {
  if (!deal || !Array.isArray(requests) || requests.length === 0) return [];
  const now = _parseIso(nowIso) || Date.now();
  const byRole = new Map();
  for (const r of requests) {
    const status = String(r?.status || "").toLowerCase();
    const isOpen = status === "pending" || status === "requested" || status === "outstanding" || status === "";
    if (!isOpen) continue;
    const role = ownerFor(r);
    const ageDays = Math.max(0, (now - (_parseIso(r.created_at) || now)) / MS_PER_DAY);
    const name = r.requested_from_name || null;
    const slot = byRole.get(role) || { role, names: new Set(), outstandingCount: 0, oldestDays: 0 };
    if (name) slot.names.add(name);
    slot.outstandingCount += 1;
    slot.oldestDays = Math.max(slot.oldestDays, ageDays);
    byRole.set(role, slot);
  }
  const list = Array.from(byRole.values()).map((slot) => ({
    role:             slot.role,
    names:            Array.from(slot.names),
    outstandingCount: slot.outstandingCount,
    oldestDays:       Math.round(slot.oldestDays * 10) / 10,
    severity:         slot.oldestDays >= 7 ? "rose"
                     : slot.oldestDays >= 3 ? "amber"
                     : "emerald",
  }));
  list.sort((a, b) => b.oldestDays - a.oldestDays);
  return list;
}

/* ─── Participant-derived signals for iter-281 ──────────────────
 *
 * Returns a compact envelope of participant-attribution signals
 * the health scorer consumes when accountability is enabled.
 *
 *   • solicitorUnresponsiveDays — max age of any open request
 *     owned by a Buyer or Seller Solicitor. Drives the
 *     "silent solicitor" penalty in iter-281.
 *   • brokerUnresponsiveDays — same for Mortgage Broker.
 *   • participantsBlocking — count of distinct roles currently
 *     blocking the deal.
 *   • sellerSolicitorIsBlocking / buyerSolicitorIsBlocking —
 *     booleans that drive role-specific scorer penalties when
 *     present alongside imminent exchange.
 */
export function participantSignalsFor({ deal, requests, nowIso } = {}) {
  const blocking = blockingParticipantsFor({ deal, requests, nowIso });
  const legal = blocking.filter((b) => LEGAL_ROLES.has(b.role));
  const broker = blocking.find((b) => b.role === "Mortgage Broker");
  return {
    solicitorUnresponsiveDays: legal.reduce((m, b) => Math.max(m, b.oldestDays), 0),
    brokerUnresponsiveDays:    broker ? broker.oldestDays : 0,
    participantsBlocking:      blocking.length,
    sellerSolicitorIsBlocking: blocking.some((b) => b.role === "Seller Solicitor"),
    buyerSolicitorIsBlocking:  blocking.some((b) => b.role === "Buyer Solicitor"),
    brokerIsBlocking:          !!broker,
  };
}

/* ─── Activity-row participant decoration ────────────────────────
 *
 * Attaches `actor_participant_role` and (when available)
 * `actor_participant_name` to an activity row so the feed can
 * group/filter by accountability. Pure — does not mutate
 * the input row. */
export function decorateActivityWithParticipant(row, request) {
  if (!row) return row;
  const role = request ? ownerFor(request) : null;
  if (!role) return row;
  return {
    ...row,
    actor_participant_role: role,
    actor_participant_name: request?.requested_from_name || null,
  };
}

/* ─── Feature-flag helper ──────────────────────────────────────── */
export function isParticipantAccountabilityEnabled() {
  return String(
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_PARTICIPANT_ACCOUNTABILITY_V1)
      || ""
  ).toLowerCase() === "true";
}
